/**
 * Обёртка над `Notification` §8/§9 (1.20), design.md решение 3. Разрешение
 * запрашивается только из обработчика жеста пользователя (`request()`
 * зовёт вызывающий из клика по кнопке, не сам модуль): Firefox и Safari
 * не показывают системный запрос без него.
 *
 * Конструктор подменяется в тестах (`notificationCtor`), по умолчанию —
 * глобальный `Notification`, которого может не быть вовсе — тогда
 * `permission()` отдаёт `"unsupported"`, а не бросает.
 *
 * Показ нотификации (`new Notification(...)`) обёрнут в `try/catch`: в
 * Chrome на Android конструктор бросает даже при выданном разрешении
 * (ревью stage6-alert-sound, пункт 5).
 *
 * Разрешение может смениться и вне этой вкладки — в настройках браузера.
 * Если доступен `navigator.permissions.query`, обёртка подписывается на его
 * `onchange` и уведомляет подписчиков `subscribe()` (пункт 8); отказ
 * `query()` (браузер не поддерживает запрос статуса нотификаций) глотается.
 */

/** Минимальная форма экземпляра `Notification`, которой пользуется модуль. */
export interface AlertNotificationLike {
  onclick: (() => void) | null;
  close(): void;
}

/** Минимальная форма конструктора `Notification` — статика и `new`. */
export interface NotificationCtorLike {
  new (title: string, options?: { body?: string; tag?: string }): AlertNotificationLike;
  readonly permission: "default" | "granted" | "denied";
  requestPermission(): Promise<"default" | "granted" | "denied">;
}

/** Статус разрешения §9 (1.20), плюс признак отсутствия `Notification` в браузере. */
export type AlertNotificationPermission = "default" | "granted" | "denied" | "unsupported";

/** Минимальная форма `PermissionStatus`, которой пользуется этот модуль. */
export interface AlertPermissionStatus {
  onchange: (() => void) | null;
}

/** Минимальная форма `navigator.permissions`, которой пользуется этот модуль. */
export interface AlertPermissions {
  query(descriptor: { name: "notifications" }): Promise<AlertPermissionStatus>;
}

export interface CreateAlertNotifyOptions {
  /** Конструктор — подменяется в тестах. По умолчанию глобальный `Notification`, если он есть. */
  notificationCtor?: NotificationCtorLike;
  /** Окно, куда щелчок по нотификации возвращает фокус. По умолчанию глобальный `window`. */
  windowRef?: { focus(): void };
  /** Источник статуса разрешения — подменяется в тестах. По умолчанию `navigator.permissions`. */
  permissionsRef?: AlertPermissions;
}

export interface AlertNotify {
  /** Текущее разрешение браузера, `"unsupported"` — если `Notification` нет вовсе. */
  permission(): AlertNotificationPermission;
  /** Запрашивает разрешение (только по жесту пользователя) и отдаёт новый статус. */
  request(): Promise<AlertNotificationPermission>;
  /** Показывает нотификацию — только при уже выданном разрешении. */
  show(title: string, body: string, tag: string): void;
  /** Подписка на внешнюю смену разрешения (настройки браузера). Возвращает функцию отписки. */
  subscribe(listener: () => void): () => void;
}

export function createAlertNotify(options: CreateAlertNotifyOptions = {}): AlertNotify {
  const ctor =
    options.notificationCtor ?? (typeof Notification !== "undefined" ? Notification : undefined);
  const windowRef = options.windowRef ?? (typeof window !== "undefined" ? window : undefined);
  const permissionsRef =
    options.permissionsRef ??
    (typeof navigator !== "undefined" ? navigator.permissions : undefined);

  const listeners = new Set<() => void>();
  let statusWatchStarted = false;

  /** Подписывается на `PermissionStatus.onchange` при первом подписчике, не раньше. */
  function watchPermissionStatus(): void {
    if (statusWatchStarted || !permissionsRef) {
      return;
    }
    statusWatchStarted = true;
    permissionsRef
      .query({ name: "notifications" })
      .then((status) => {
        status.onchange = () => {
          for (const listener of listeners) {
            listener();
          }
        };
      })
      .catch(() => {
        // Браузер не поддерживает запрос статуса нотификаций — обёртка
        // остаётся без внешнего сигнала, `permission()` всё ещё читает
        // `ctor.permission` по запросу.
      });
  }

  function permission(): AlertNotificationPermission {
    return ctor ? ctor.permission : "unsupported";
  }

  return {
    permission,
    async request() {
      if (!ctor) {
        return "unsupported";
      }
      return ctor.requestPermission();
    },
    show(title, body, tag) {
      if (ctor?.permission !== "granted") {
        return;
      }
      try {
        const instance = new ctor(title, { body, tag });
        instance.onclick = () => {
          windowRef?.focus();
          instance.close();
        };
      } catch (error) {
        console.error("не удалось показать нотификацию", error);
      }
    },
    subscribe(listener) {
      listeners.add(listener);
      watchPermissionStatus();
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
